// One-way channel from the raid's resolution code to the map, and the timing
// maths for what it draws.
//
// A skill check resolving is the raid's whole beat, and until now the map said
// nothing about it — the log scrolled and the tiles carried on as they were.
// `processSkillCheckEvent` knows the outcome, whether a synergy variant fired,
// who took the hit and what was salvaged; it posts that here, and the map picks
// it up on its next frame.
//
// A QUEUE rather than a callback, for two reasons. The resolution code is
// headless — it runs in the simulator and under vitest with no renderer at all
// — so it must not hold a reference to one. And the map animates at 12fps while
// the raid loop ticks at 1Hz, so the two are never in the same call stack
// anyway. Nothing here touches the DOM.

/**
 * How long each effect lives, in ms.
 *
 * A synergy runs longest because it has the most to say and is the rarest
 * thing on the list; a failure is the shortest because it is a flinch. All of
 * them are well under the ~1s the raid loop takes to move on, so effects
 * belong to the beat that caused them rather than smearing across the next.
 */
export const FX_TTL = {
    success: 900,
    synergy: 1400,
    failure: 700,
    loot: 1100,
};

// The queue is bounded because nothing guarantees anyone drains it: a raid run
// in the simulator, or with the Field screen closed, posts effects that will
// never be drawn. Dropping the OLDEST is deliberate — if the map does come
// back, the newest handful is what it should be showing.
const QUEUE_CAP = 24;

let queue = [];

/**
 * Post an effect for the map to pick up.
 *
 * @param kind      one of FX_TTL's keys
 * @param opts.memberId  whose effect it is; the map matches it against the
 *                  live roster and falls back to the party's centre if that
 *                  member is not on screen (dead, or the roster changed)
 * @param opts.rarity    for `loot`, which tints the mote
 * @param opts.delayMs   held back this long before it starts, so three items
 *                  recovered at once arrive as three beats rather than one
 * @param opts.nowMs     injectable clock, for tests
 */
export function emitRaidFx(kind, opts = {}) {
    if (!FX_TTL[kind]) return null;
    const fx = {
        kind,
        memberId: opts.memberId ?? null,
        rarity: opts.rarity || null,
        startMs: (opts.nowMs ?? Date.now()) + (opts.delayMs || 0),
        ttl: FX_TTL[kind],
        // Decorrelates one effect's sparks from the next's without a per-frame
        // random, which would make them shimmer instead of move.
        seed: (queue.length * 2654435761 + (opts.memberId ? String(opts.memberId).length * 40503 : 0)) >>> 0,
    };
    queue.push(fx);
    if (queue.length > QUEUE_CAP) queue.splice(0, queue.length - QUEUE_CAP);
    return fx;
}

/** Take everything posted since the last call. */
export function drainRaidFx() {
    if (!queue.length) return [];
    const out = queue;
    queue = [];
    return out;
}

/** Throw the queue away — a raid ending, or a grid being rebuilt. */
export function clearRaidFx() {
    queue = [];
}

/**
 * How far through its life an effect is: 0 at the start, 1 at the end.
 *
 * Negative while it is still waiting out its `delayMs`, which is how a
 * staggered burst of loot motes holds each one back without a timer per mote.
 */
export function fxPhase(fx, nowMs) {
    return (nowMs - fx.startMs) / fx.ttl;
}

/** The ones still worth drawing: started or about to, and not yet finished. */
export function liveFx(list, nowMs) {
    return (list || []).filter(fx => fxPhase(fx, nowMs) < 1);
}

/**
 * Ease for a one-shot: quick to arrive, slow to leave.
 *
 * Everything drawn here is a flash of some kind, and a flash that fades
 * linearly reads as a fill being turned down rather than as light going out.
 */
// `p < 0` rather than `p <= 0`: a delayed effect is dark until its turn, but
// one that has just started is at full brightness, and those are the same
// number from different sides.
export const fxFade = p => (p < 0 ? 0 : p >= 1 ? 0 : Math.pow(1 - p, 1.8));

/** Ease for a ring or a mote travelling outward: fast then settling. */
export const fxTravel = p => (p < 0 ? 0 : p >= 1 ? 1 : 1 - Math.pow(1 - p, 2.4));
